import React from "react";

interface DeleteThemeModalProps {
  isOpen: boolean;
  themeMode?: "dark" | "light"; // renamed to avoid clashing with 'theme' conceptually
  themeName?: string; // Optional: Show the specific name of the theme being deleted
  isDeleting?: boolean; // Useful for showing a loading state on the button
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteThemeModal: React.FC<DeleteThemeModalProps> = ({
  isOpen,
  themeMode = "dark",
  themeName,
  isDeleting = false,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${
        themeMode === "dark" ? "bg-black/70" : "bg-black/40"
      }`}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
          themeMode === "dark"
            ? "bg-[#1A1A1A] text-gray-100 border-[#333333]"
            : "bg-white text-gray-900 border-gray-200"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isDeleting}
          aria-label="Close"
          className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${
            themeMode === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
          } ${isDeleting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M12.423 2.63525L2.63477 12.4235M2.63477 2.63525L12.423 12.4235"
              stroke={themeMode === "dark" ? "#F3F3F6" : "#2E2E2E"}
              strokeWidth="1.12942"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="px-6 py-8 text-center">
          {/* Trash Icon */}
          <div
            className={`mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-full ${
              themeMode === "dark" ? "bg-[#111111] border border-[#2A2A2A]" : "bg-gray-100"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M19.5 5.5L19.0982 12.0062M4.5 5.5L5.10461 15.5248C5.25945 18.0922 5.33688 19.3759 5.97868 20.299C6.296 20.7554 6.7048 21.1407 7.17905 21.4302C7.85035 21.84 8.68108 21.9631 10 22"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M20 15L13 21.9995M20 22L13 15.0005"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <h3 className="text-xl font-semibold mb-2">Delete Theme</h3>
          <p
            className={`${
              themeMode === "dark" ? "text-gray-400" : "text-gray-600"
            } mb-8 text-sm`}
          >
            Are you sure you want to delete {themeName ? <strong>"{themeName}"</strong> : "this theme"}? <br />
            This action cannot be undone.
          </p>

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className={`w-[130px] h-[42px] font-medium text-white rounded-lg border border-[#AD1F1F] bg-gradient-to-b from-[#D92626CC] to-[#5B1010CC] transition-opacity flex items-center justify-center ${
                isDeleting ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
              }`}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className={`w-[130px] h-[42px] font-medium text-white rounded-lg border border-[#B6CB24] bg-gradient-to-b from-[#C6D92CCC] to-[#535B10CC] transition-opacity ${
                isDeleting ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteThemeModal;